import { useState, useMemo } from "react";
import { Plus, Filter, DollarSign, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  pointerWithin,
  rectIntersection,
  getFirstCollision,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
  type CollisionDetection,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates, arrayMove } from "@dnd-kit/sortable";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useProjects, useUpdateProject, useProjectHours } from "@/hooks/useProjects";
import { useCreateProjectWithPayments } from "@/hooks/useProjectWithPayments";
import { useClients } from "@/hooks/useClients";
import { useTimeEntries } from "@/hooks/useTimeEntries";
import { useProfile } from "@/hooks/useProfile";
import { useAuth } from "@/hooks/useAuth";
import { usePlan } from "@/hooks/usePlan";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { format, differenceInDays, addDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { KanbanColumn } from "@/components/kanban/KanbanColumn";
import { SortableProjectCard } from "@/components/kanban/SortableProjectCard";
import { ProjectDragOverlay } from "@/components/kanban/DragOverlay";
import { PaymentSetupSection, type PaymentSetupData } from "@/components/projects/PaymentSetupSection";
import type { Database } from "@/integrations/supabase/types";
import { cn } from "@/lib/utils";

type ProjectStatus = Database["public"]["Enums"]["project_status"];
type BillingType = "hourly" | "fixed";

interface ProjectFormData {
  name: string;
  description: string;
  client_id: string;
  deadline: string;
  status: ProjectStatus;
  billing_type: BillingType;
  hourly_rate: string;
  estimated_hours: string;
  fixed_price: string;
  completion_percentage: number;
}

const columns: { id: ProjectStatus; title: string }[] = [
  { id: "backlog", title: "Backlog" },
  { id: "in_progress", title: "Em Andamento" },
  { id: "review", title: "Revisão" },
  { id: "done", title: "Concluído" },
];

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const { data: clients } = useClients();
  const { data: timeEntries } = useTimeEntries();
  const { data: profile } = useProfile();
  const { isLoading: planLoading } = usePlan();
  const createProject = useCreateProjectWithPayments();
  const updateProject = useUpdateProject();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeProject, setActiveProject] = useState<typeof projects extends (infer T)[] | undefined ? T | null : null>(null);
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [billingFilter, setBillingFilter] = useState<string>("all");
  
  // Completion confirmation dialog
  const [completionDialog, setCompletionDialog] = useState<{
    open: boolean;
    projectId: string;
    projectName: string;
    fixedPrice: number;
    hoursSpent: number;
  } | null>(null);

  const [formData, setFormData] = useState<ProjectFormData>({
    name: "",
    description: "",
    client_id: "",
    deadline: "",
    status: "backlog",
    billing_type: "hourly",
    hourly_rate: "",
    estimated_hours: "",
    fixed_price: "",
    completion_percentage: 0,
  });

  const defaultPaymentSetup = (): PaymentSetupData => ({
    createPayments: true,
    paymentPlan: 'full',
    installmentConfig: {
      count: 3,
      intervalDays: 30,
      firstDueDate: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    },
    recurringConfig: {
      dayOfMonth: new Date().getDate(),
      firstDueDate: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    },
  });

  const [paymentSetup, setPaymentSetup] = useState<PaymentSetupData>(defaultPaymentSetup());

  // Count total projects (regardless of status)
  const totalProjectCount = useMemo(() => {
    return projects?.length || 0;
  }, [projects]);

  // Acesso garantido pelo ProtectedRoute - sem limite de projetos
  const handleNewProjectClick = () => {
    setIsDialogOpen(true);
  };
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Calculate hours spent per project with billable breakdown
  const projectHoursMap = useMemo(() => {
    const map: Record<string, { total: number; billable: number; nonBillable: number; billableValue: number }> = {};
    timeEntries?.forEach((entry) => {
      const projectId = entry.project_id;
      if (projectId) {
        if (!map[projectId]) {
          map[projectId] = { total: 0, billable: 0, nonBillable: 0, billableValue: 0 };
        }
        const hours = (entry.duration_minutes || 0) / 60;
        const rate = entry.hourly_rate || 0;
        
        map[projectId].total += hours;
        
        if (entry.is_billable !== false) {
          map[projectId].billable += hours;
          map[projectId].billableValue += hours * rate;
        } else {
          map[projectId].nonBillable += hours;
        }
      }
    });
    return map;
  }, [timeEntries]);

  // Filter and group projects
  const filteredProjects = useMemo(() => {
    let filtered = projects || [];
    
    if (clientFilter !== "all") {
      filtered = filtered.filter((p) => p.client_id === clientFilter);
    }
    
    if (billingFilter !== "all") {
      filtered = filtered.filter((p) => (p as any).billing_type === billingFilter);
    }
    
    return filtered;
  }, [projects, clientFilter, billingFilter]);

  const projectsByStatus = useMemo(() => {
    const grouped: Record<ProjectStatus, typeof projects> = {
      backlog: [],
      in_progress: [],
      review: [],
      done: [],
    };

    filteredProjects?.forEach((project) => {
      const status = project.status || "backlog";
      grouped[status]?.push(project);
    });

    return grouped;
  }, [filteredProjects]);

  const formatDeadline = (deadline: string | null) => {
    if (!deadline) return "";
    try {
      return format(new Date(deadline), "dd MMM", { locale: ptBR });
    } catch {
      return "";
    }
  };

  const isDeadlineNear = (deadline: string | null, status?: string) => {
    if (!deadline) return false;
    // Don't show deadline warning for completed projects
    if (status === "done") return false;
    const days = differenceInDays(new Date(deadline), new Date());
    return days >= 0 && days <= 7;
  };

  const handleDragStart = (event: DragStartEvent) => {
    const projectId = event.active.id as string;
    const project = projects?.find((p) => p.id === projectId);
    setActiveProject(project || null);
  };

  // Custom collision detection that prioritizes columns
  const collisionDetectionStrategy: CollisionDetection = (args) => {
    // First, check for pointer collision with droppable columns
    const pointerCollisions = pointerWithin(args);
    
    // Find if we're over a column
    const columnIds = columns.map(c => c.id);
    const overColumn = pointerCollisions.find(
      collision => columnIds.includes(collision.id as ProjectStatus)
    );
    
    if (overColumn) {
      return [overColumn];
    }

    // If not over a column directly, check which column the pointer is in
    // by checking rect intersection with columns
    const rectCollisions = rectIntersection(args);
    const columnCollision = rectCollisions.find(
      collision => columnIds.includes(collision.id as ProjectStatus)
    );
    
    if (columnCollision) {
      return [columnCollision];
    }

    // Fallback to closest center for items
    return closestCenter(args);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveProject(null);

    if (!over) return;

    const projectId = active.id as string;
    let newStatus: ProjectStatus | null = null;

    // Check if dropped directly on a column
    if (columns.find((col) => col.id === over.id)) {
      newStatus = over.id as ProjectStatus;
    } else {
      // Dropped on another project card - find which column it belongs to
      const overProject = projects?.find((p) => p.id === over.id);
      if (overProject) {
        newStatus = overProject.status || "backlog";
      }
    }

    if (!newStatus) return;

    const project = projects?.find((p) => p.id === projectId);
    if (!project || project.status === newStatus) return;

    // Check if moving to "done" and is fixed price
    if (newStatus === "done" && (project as any).billing_type === "fixed") {
      const projectStats = projectHoursMap[projectId];
      const hoursSpent = projectStats?.total || 0;
      setCompletionDialog({
        open: true,
        projectId,
        projectName: project.name,
        fixedPrice: (project as any).fixed_price || 0,
        hoursSpent: Math.round(hoursSpent * 10) / 10,
      });
      return;
    }

    try {
      await updateProject.mutateAsync({
        id: projectId,
        status: newStatus,
      });
      toast({
        title: "Projeto atualizado",
        description: `Movido para ${columns.find((c) => c.id === newStatus)?.title}`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar o projeto.",
      });
    }
  };

  const handleConfirmCompletion = async () => {
    if (!completionDialog) return;
    
    try {
      await updateProject.mutateAsync({
        id: completionDialog.projectId,
        status: "done" as ProjectStatus,
        completion_percentage: 100,
      } as any);
      
      toast({
        title: "Projeto concluído!",
        description: "O projeto foi marcado como concluído.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível concluir o projeto.",
      });
    }
    
    setCompletionDialog(null);
  };

  const handleSubmit = async () => {
    if (!user?.id) return;

    if (!formData.name.trim()) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Nome do projeto é obrigatório.",
      });
      return;
    }

    // Validate based on billing type
    if (formData.billing_type === "hourly") {
      if (!formData.estimated_hours || parseFloat(formData.estimated_hours) <= 0) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Projetos por hora precisam ter horas estimadas maiores que 0.",
        });
        return;
      }
    } else {
      if (!formData.fixed_price || parseFloat(formData.fixed_price) <= 0) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Projetos de preço fixo precisam ter valor definido maior que 0.",
        });
        return;
      }
    }

    try {
      await createProject.mutateAsync({
        project: {
          user_id: user.id,
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          client_id: formData.client_id || null,
          deadline: formData.deadline || null,
          status: formData.status,
          billing_type: formData.billing_type,
          hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
          estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
          fixed_price: formData.fixed_price ? parseFloat(formData.fixed_price) : null,
          completion_percentage: formData.billing_type === "fixed" ? formData.completion_percentage : null,
        },
        paymentSetup: formData.billing_type === "fixed" ? paymentSetup : null,
      });

      setFormData({
        name: "",
        description: "",
        client_id: "",
        deadline: "",
        status: "backlog",
        billing_type: "hourly",
        hourly_rate: "",
        estimated_hours: "",
        fixed_price: "",
        completion_percentage: 0,
      });
      setPaymentSetup(defaultPaymentSetup());
      setIsDialogOpen(false);
    } catch (error: any) {
      // Error is handled by the mutation
    }
  };

  // Get default hourly rate from selected client or profile
  const getDefaultHourlyRate = (clientId: string) => {
    const client = clients?.find((c) => c.id === clientId);
    return client?.hourly_rate || profile?.hourly_rate || null;
  };

  // Update hourly rate when client changes
  const handleClientChange = (clientId: string) => {
    const defaultRate = getDefaultHourlyRate(clientId);
    setFormData((prev) => ({
      ...prev,
      client_id: clientId,
      hourly_rate: defaultRate?.toString() || prev.hourly_rate,
    }));
  };

  // Calculate estimated value for hourly projects
  const estimatedValue = useMemo(() => {
    if (formData.billing_type !== "hourly") return null;
    const hours = parseFloat(formData.estimated_hours) || 0;
    const rate = parseFloat(formData.hourly_rate) || 0;
    return hours * rate;
  }, [formData.billing_type, formData.estimated_hours, formData.hourly_rate]);

  return (
    <DashboardLayout title="Projetos">
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-muted-foreground">
            Arraste os projetos entre as colunas para atualizar o status
          </p>
          <Button className="gap-2 w-full sm:w-auto" onClick={handleNewProjectClick}>
            <Plus className="h-4 w-4" />
            Novo Projeto
          </Button>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Novo Projeto</DialogTitle>
                <DialogDescription>
                  Preencha as informações do novo projeto
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Projeto *</Label>
                  <Input
                    id="name"
                    placeholder="Ex: Website Redesign"
                    value={formData.name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea
                    id="description"
                    placeholder="Descreva o projeto..."
                    value={formData.description}
                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="client">Cliente</Label>
                    <Select
                      value={formData.client_id}
                      onValueChange={handleClientChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients?.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deadline">Prazo</Label>
                    <Input
                      id="deadline"
                      type="date"
                      value={formData.deadline}
                      onChange={(e) => setFormData((prev) => ({ ...prev, deadline: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Billing Type Selection */}
                <div className="space-y-3">
                  <Label>Como você cobra este projeto? *</Label>
                  <RadioGroup
                    value={formData.billing_type}
                    onValueChange={(value: BillingType) => setFormData((prev) => ({ ...prev, billing_type: value }))}
                    className="grid gap-3"
                  >
                    <div
                      className={cn(
                        "flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors",
                        formData.billing_type === "hourly" ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                      )}
                      onClick={() => setFormData((prev) => ({ ...prev, billing_type: "hourly" }))}
                    >
                      <RadioGroupItem value="hourly" id="hourly" className="mt-1" />
                      <div className="flex-1">
                        <label htmlFor="hourly" className="font-medium cursor-pointer flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Por Hora
                        </label>
                        <p className="text-sm text-muted-foreground">
                          Você rastreia tempo e cobra por hora trabalhada
                        </p>
                      </div>
                    </div>
                    <div
                      className={cn(
                        "flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors",
                        formData.billing_type === "fixed" ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                      )}
                      onClick={() => setFormData((prev) => ({ ...prev, billing_type: "fixed" }))}
                    >
                      <RadioGroupItem value="fixed" id="fixed" className="mt-1" />
                      <div className="flex-1">
                        <label htmlFor="fixed" className="font-medium cursor-pointer flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          Preço Fixo
                        </label>
                        <p className="text-sm text-muted-foreground">
                          Valor fechado independente das horas trabalhadas
                        </p>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                {/* Conditional Fields based on billing type */}
                {formData.billing_type === "hourly" ? (
                  <div className="space-y-4 rounded-lg border p-4 bg-muted/30">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="hourly_rate">Valor/Hora (R$)</Label>
                        <Input
                          id="hourly_rate"
                          type="number"
                          placeholder="100.00"
                          value={formData.hourly_rate}
                          onChange={(e) => setFormData((prev) => ({ ...prev, hourly_rate: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="estimated_hours">Horas Estimadas *</Label>
                        <Input
                          id="estimated_hours"
                          type="number"
                          placeholder="40"
                          value={formData.estimated_hours}
                          onChange={(e) => setFormData((prev) => ({ ...prev, estimated_hours: e.target.value }))}
                        />
                      </div>
                    </div>
                    {estimatedValue && estimatedValue > 0 && (
                      <div className="text-sm text-muted-foreground bg-background rounded-md p-3">
                        <span className="font-medium">Valor Estimado: </span>
                        {formData.estimated_hours}h × {formatCurrency(parseFloat(formData.hourly_rate))} = {" "}
                        <span className="font-semibold text-foreground">{formatCurrency(estimatedValue)}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4 rounded-lg border p-4 bg-muted/30">
                    <div className="space-y-2">
                      <Label htmlFor="fixed_price">Valor do Projeto (R$) *</Label>
                      <Input
                        id="fixed_price"
                        type="number"
                        placeholder="5000.00"
                        value={formData.fixed_price}
                        onChange={(e) => setFormData((prev) => ({ ...prev, fixed_price: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="estimated_hours_fixed">Horas Estimadas (opcional)</Label>
                      <Input
                        id="estimated_hours_fixed"
                        type="number"
                        placeholder="50"
                        value={formData.estimated_hours}
                        onChange={(e) => setFormData((prev) => ({ ...prev, estimated_hours: e.target.value }))}
                      />
                      <p className="text-xs text-muted-foreground">
                        Opcional: para calcular sua taxa/hora real após conclusão
                      </p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Percentual de Conclusão</Label>
                        <span className="text-sm font-medium">{formData.completion_percentage}%</span>
                      </div>
                      <Slider
                        value={[formData.completion_percentage]}
                        onValueChange={([value]) => setFormData((prev) => ({ ...prev, completion_percentage: value }))}
                        max={100}
                        step={5}
                      />
                    </div>
                  </div>
                )}

                {/* Payment Setup Section - Only for fixed price projects */}
                {formData.billing_type === "fixed" && (
                  <PaymentSetupSection
                    projectValue={parseFloat(formData.fixed_price) || 0}
                    billingType={formData.billing_type}
                    value={paymentSetup}
                    onChange={setPaymentSetup}
                    disabled={createProject.isPending}
                  />
                )}

                <div className="space-y-2">
                  <Label htmlFor="status">Status Inicial</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, status: value as ProjectStatus }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {columns.map((col) => (
                        <SelectItem key={col.id} value={col.id}>
                          {col.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter className="flex-col gap-2 sm:flex-row">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="w-full sm:w-auto">
                  Cancelar
                </Button>
                <Button onClick={handleSubmit} disabled={createProject.isPending} className="w-full sm:w-auto">
                  {createProject.isPending ? "Criando..." : "Criar Projeto"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Todos os clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os clientes</SelectItem>
                {clients?.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Select value={billingFilter} onValueChange={setBillingFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Tipo de cobrança" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              <SelectItem value="hourly">⏱️ Por Hora</SelectItem>
              <SelectItem value="fixed">💰 Preço Fixo</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Kanban Board */}
        {projectsLoading ? (
          <div className="grid gap-6 lg:grid-cols-4">
            {columns.map((col) => (
              <div key={col.id} className="space-y-4">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-32" />
                <Skeleton className="h-32" />
              </div>
            ))}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetectionStrategy}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {columns.map((column) => {
                const columnProjects = projectsByStatus[column.id] || [];
                return (
                  <KanbanColumn
                    key={column.id}
                    id={column.id}
                    title={column.title}
                    count={columnProjects.length}
                    items={columnProjects.map((p) => p.id)}
                    variant="project"
                  >
                    {columnProjects.length > 0 ? (
                      columnProjects.map((project) => {
                        const client = project.clients as { id: string; name: string } | null;
                        const projectStats = projectHoursMap[project.id];
                        const hoursSpent = projectStats?.total || 0;
                        const billableHours = projectStats?.billable || 0;
                        const nonBillableHours = projectStats?.nonBillable || 0;
                        const billableValue = projectStats?.billableValue || 0;
                        const billingType = (project as any).billing_type || "hourly";
                        const fixedPrice = (project as any).fixed_price;
                        const completionPercentage = (project as any).completion_percentage || 0;
                        const estimatedHours = project.estimated_hours || 0;
                        const hourlyRate = project.hourly_rate || 0;
                        
                        return (
                          <SortableProjectCard
                            key={project.id}
                            id={project.id}
                            name={project.name}
                            client={client?.name || "Sem cliente"}
                            deadline={formatDeadline(project.deadline)}
                            deadlineNear={isDeadlineNear(project.deadline, project.status)}
                            hoursSpent={Math.round(hoursSpent * 10) / 10}
                            billableHours={Math.round(billableHours * 100) / 100}
                            nonBillableHours={Math.round(nonBillableHours * 100) / 100}
                            billableValue={Math.round(billableValue * 100) / 100}
                            status={project.status || "backlog"}
                            billingType={billingType}
                            fixedPrice={fixedPrice}
                            completionPercentage={completionPercentage}
                            estimatedHours={estimatedHours}
                            hourlyRate={hourlyRate}
                            onClick={() => navigate(`/projects/${project.id}`)}
                          />
                        );
                      })
                    ) : (
                      <div className="flex h-24 items-center justify-center rounded-lg border-2 border-dashed text-sm text-muted-foreground">
                        Nenhum projeto
                      </div>
                    )}
                  </KanbanColumn>
                );
              })}
            </div>

            <DragOverlay>
              {activeProject && (
                <ProjectDragOverlay
                  name={activeProject.name}
                  client={(activeProject.clients as { name: string } | null)?.name || "Sem cliente"}
                  deadline={formatDeadline(activeProject.deadline)}
                  hoursSpent={projectHoursMap[activeProject.id]?.total || 0}
                  status={activeProject.status || "backlog"}
                />
              )}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Completion Confirmation Dialog */}
      <AlertDialog open={!!completionDialog} onOpenChange={(open) => !open && setCompletionDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>🎉 Projeto Concluído!</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Confirmar conclusão do projeto <strong>{completionDialog?.projectName}</strong>?</p>
                <div className="rounded-lg bg-muted p-3 space-y-1 text-sm">
                  <p><strong>Valor:</strong> {formatCurrency(completionDialog?.fixedPrice || 0)}</p>
                  <p><strong>Horas gastas:</strong> {completionDialog?.hoursSpent}h</p>
                  {completionDialog?.hoursSpent && completionDialog.hoursSpent > 0 && (
                    <p><strong>Taxa real:</strong> {formatCurrency((completionDialog?.fixedPrice || 0) / completionDialog.hoursSpent)}/h</p>
                  )}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCompletion}>
              Concluir Projeto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    
    </DashboardLayout>
  );
}
